import React, {useCallback, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Flex, Text} from '@chakra-ui/react'
import useResizeObserver from '../../hooks/use-resize-observer'
import {FormattedMessage} from 'react-intl'

/**
 * Render a section limited to a certain number of lines on mobile, with a button to expand and collapse the full content.
 *
 * On desktop the content is always fully displayed.
 *
 * By default, the children are rendered within a Text component, and all extra parameters are passed through to it,
 * including `as` to change it to a different component.
 */
const ExpandableText = ({children, maxLines, ...htmlProps}) => {
    // reference to the children container element to check for overflow
    const [childrenContainer, setChildrenContainer] = useState(null)
    // state to track if the component is in expanded state
    const [expanded, setExpanded] = useState(false)
    // state to trigger rerender when the component is resized
    const [, rerender] = useState(0)

    const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), [])

    // use resize observer to trigger rerender when the component is resized
    useResizeObserver(childrenContainer, () => {
        // trigger rerender to check for overflow
        rerender((prev) => prev + 1)
    })

    // check if the children content is overflowing the container,
    // in expanded state consider it's overflowing as in that case the container is always as large as the content,
    // but we can always be in that state if it was overflowing before
    const isOverflowing =
        expanded || childrenContainer?.scrollHeight > childrenContainer?.clientHeight

    return (
        <Flex flexDirection="column" gap={2}>
            <Text
                {...htmlProps}
                ref={setChildrenContainer}
                sx={
                    !expanded && {
                        WebkitLineClamp: maxLines,
                        WebkitBoxOrient: 'vertical',
                        overflow: {base: 'hidden', lg: htmlProps?.overflow || 'initial'},
                        textOverflow: 'ellipsis',
                        display: {base: '-webkit-box', lg: htmlProps?.display || 'initial'}
                    }
                }
            >
                {children}
            </Text>

            {isOverflowing && (
                <Button
                    display={{lg: 'none'}}
                    variant="link"
                    alignSelf="flex-start"
                    onClick={toggleExpanded}
                >
                    {expanded ? (
                        <FormattedMessage
                            id="expandable_text.collapse.label"
                            defaultMessage="Read less"
                        />
                    ) : (
                        <FormattedMessage
                            id="expandable_text.expand.label"
                            defaultMessage="Read more"
                        />
                    )}
                </Button>
            )}
        </Flex>
    )
}

ExpandableText.propTypes = {
    children: PropTypes.node,
    maxLines: PropTypes.number,
    textStyle: PropTypes.string
}

export default ExpandableText
